import { createResource } from "redux-rest-resource";
import { setAuthHeader, getAuthData } from "../../helpers/authStorage";
import { API_URL } from "../../constants/config";

// Helper reducer to update order ticket state
const updateOrderNewTicketStateReduce = (state, action) => {
  if (action.status === "resolved") {
    return {
      ...state,
      items: state.items.map((stateItem) => {
        return {
          ...stateItem,
          items: stateItem.items.map((i) => {
            if (i.id === action.context.orderId) {
              return {
                ...i,
                ticket: {
                  ...action.context.ticket,
                },
              };
            }
            return i;
          }),
        };
      }),
    };
  }
  return { ...state };
};

// ============================================================================
// Shared reducer helper — updates a single order's fields in nested state
// Used by cancel, postpone, and any action that patches one order in place
// ============================================================================
const updateSingleOrderReduce = (orderId, fields, state) => {
  return {
    ...state,
    items: state.items.map((stateItem) => ({
      ...stateItem,
      items: stateItem.items.map((order) =>
        order.id === orderId ? { ...order, ...fields } : order
      ),
    })),
  };
};

export const { types, actions, rootReducer } = createResource({
  name: "adminOrder",
  url: `${API_URL}/admin/orders`,

  actions: {
    // Fetch all orders with pagination
    fetch: {
      transformResponse: (res) => ({ ...res, body: res.body }),
    },

    // Get single order by ID
    getById: {
      method: "GET",
      url: `${API_URL}/admin/orders/:orderId`,
    },

    // Save uploaded orders
    saveUploaded: {
      method: "POST",
    },

    // ============================================================================
    // Cancel an order — now updates Redux state immediately on resolve
    // ============================================================================
    cancel: {
      method: "PUT",
      url: `${API_URL}/admin/orders/:orderId/cancel`,
      reduce: (state, action) => {
        if (action.status === "resolved") {
          return updateSingleOrderReduce(
            action.context.orderId,
            { status: "cancelled" },
            state
          );
        }
        return { ...state };
      },
    },

    // ============================================================================
    // Postpone an order — now updates Redux state immediately on resolve
    // ============================================================================
    postpone: {
      method: "PUT",
      url: `${API_URL}/admin/orders/:orderId/postpone`,
      reduce: (state, action) => {
        if (action.status === "resolved") {
          return updateSingleOrderReduce(
            action.context.orderId,
            { status: "postponed" },
            state
          );
        }
        return { ...state };
      },
    },

    // Delete an order
    delete: {
      method: "DELETE",
      url: `${API_URL}/admin/orders/:orderId`,
    },

    // Restore deleted order
    restore: {
      method: "PUT",
      url: `${API_URL}/admin/orders/restore/:orderId`,
    },

    // Restore failed order to new status
    restoreFailed: {
      method: "PATCH",
      url: `${API_URL}/admin/orders/:orderId/new`,
    },

    // Create return order
    createReturn: {
      method: "POST",
      url: `${API_URL}/admin/orders/:orderId/return`,
    },

    // Complete an order
    completeOrder: {
      method: "PUT",
      url: `${API_URL}/admin/orders/:orderId/complete`,
    },

    // Cancel completed order
    cancelCompleted: {
      method: "PUT",
      url: `${API_URL}/admin/orders/:orderId/cancel-completed`,
    },

    // Bulk delete orders
    bulkDelete: {
      method: "DELETE",
      url: `${API_URL}/admin/orders`,
    },

    // Change order location
    changeLocation: {
      method: "PUT",
      url: `${API_URL}/admin/orders/:orderId/location`,
    },

    // Remove order location
    removeLocation: {
      method: "DELETE",
      url: `${API_URL}/admin/orders/:orderId/location`,
    },

    // Edit customer phone number
    editPhone: {
      method: "PUT",
      url: `${API_URL}/admin/orders/:orderId/phone-number`,
    },

    // Return order to shipper
    returnToShipper: {
      method: "PUT",
      url: `${API_URL}/admin/orders/:orderId/return-to-shipper`,
    },

    // Get job reports for order
    getReports: {
      method: "GET",
      url: `${API_URL}/admin/orders/:id/job-reports`,
    },

    // ============================================================================
    // Update Admin Note
    // API: PUT /admin/orders/:orderId/admin-note
    // Body: { adminNote: string }
    // ============================================================================
    updateAdminNote: {
      method: "PUT",
      url: `${API_URL}/admin/orders/:orderId/admin-note`,
      reduce: (state, action) => {
        if (action.status === "resolved") {
          return updateSingleOrderReduce(
            action.context.orderId,
            { adminNote: action.body.adminNote },
            state
          );
        }
        return { ...state };
      },
    },

    // Edit initial address
    editInitialAddress: {
      method: "PUT",
      url: `${API_URL}/admin/orders/:orderId/initial-address`,
    },

    // Fetch order status history
    fetchOrderHistory: {
      method: "GET",
      url: `${API_URL}/admin/orders/:orderId/status-history`,
    },

    // Fetch return shipper form
    fetchReturnShipperForm: {
      method: "GET",
      url: `${API_URL}/admin/orders/:orderId/return-form`,
    },

    // Add orders to job
    addToJob: {
      method: "PUT",
      url: `${API_URL}/admin/jobs/:jobId/orders/add`,
    },

    // Remove orders from job
    removeFromJob: {
      method: "DELETE",
      url: `${API_URL}/admin/jobs/:jobId/orders`,
    },

    // Update order status to one of the 5 operational statuses
    updateOrderStatus: {
      method: "PATCH",
      url: `${API_URL}/admin/orders/:orderId/status`,
      reduce: (state, action) => {
        if (action.status === "resolved") {
          return updateSingleOrderReduce(
            action.context.orderId,
            { status: action.context.status },
            state
          );
        }
        return { ...state };
      },
    },

    // Get jobs by status
    getJobsByStatus: {
      method: "GET",
      url: `${API_URL}/admin/jobs/status`,
      reduce: (state, action) => {
        return {
          ...state,
          jobIds: action.body,
        };
      },
    },

    // Pure Actions (Redux-only, no API)
    updateNewTicketState: {
      isPure: true,
      reduce: updateOrderNewTicketStateReduce,
    },
    updateTicketState: {
      isPure: true,
      reduce: updateOrderNewTicketStateReduce,
    },
  },
});

// Set authorization header
setAuthHeader(getAuthData().accessToken);
// import { createResource } from "redux-rest-resource";
// import { setAuthHeader, getAuthData } from "../../helpers/authStorage";
// import { API_URL } from "../../constants/config";

// // Helper reducer to update order ticket state
// const updateOrderNewTicketStateReduce = (state, action) => {
//   if (action.status === "resolved") {
//     return {
//       ...state,
//       items: state.items.map((stateItem) => {
//         return {
//           ...stateItem,
//           items: stateItem.items.map((i) => {
//             if (i.id === action.context.orderId) {
//               return {
//                 ...i,
//                 ticket: {
//                   ...action.context.ticket,
//                 },
//               };
//             }
//             return i;
//           }),
//         };
//       }),
//     };
//   }
//   return { ...state };
// };

// // Create Redux resource with all actions
// export const { types, actions, rootReducer } = createResource({
//   name: "adminOrder",
//   url: `${API_URL}/admin/orders`,

//   actions: {
//     // Fetch all orders with pagination
//     fetch: {
//       transformResponse: (res) => ({ ...res, body: res.body }),
//     },

//     // Get single order by ID
//     getById: {
//       method: "GET",
//       url: `${API_URL}/admin/orders/:orderId`,
//     },

//     // Save uploaded orders
//     saveUploaded: {
//       method: "POST",
//     },

//     // Cancel an order
//     cancel: {
//       method: "PUT",
//       url: `${API_URL}/admin/orders/:orderId/cancel`,
//     },

//     // Postpone an order
//     postpone: {
//       method: "PUT",
//       url: `${API_URL}/admin/orders/:orderId/postpone`,
//     },

//     // Delete an order
//     delete: {
//       method: "DELETE",
//       url: `${API_URL}/admin/orders/:orderId`,
//     },

//     // Restore deleted order
//     restore: {
//       method: "PUT",
//       url: `${API_URL}/admin/orders/restore/:orderId`,
//     },

//     // Restore failed order to new status
//     restoreFailed: {
//       method: "PATCH",
//       url: `${API_URL}/admin/orders/:orderId/new`,
//     },

//     // Create return order
//     createReturn: {
//       method: "POST",
//       url: `${API_URL}/admin/orders/:orderId/return`,
//     },

//     // Complete an order
//     completeOrder: {
//       method: "PUT",
//       url: `${API_URL}/admin/orders/:orderId/complete`,
//     },

//     // Cancel completed order
//     cancelCompleted: {
//       method: "PUT",
//       url: `${API_URL}/admin/orders/:orderId/cancel-completed`,
//     },

//     // Bulk delete orders
//     bulkDelete: {
//       method: "DELETE",
//       url: `${API_URL}/admin/orders`,
//     },

//     // Change order location
//     changeLocation: {
//       method: "PUT",
//       url: `${API_URL}/admin/orders/:orderId/location`,
//     },

//     // Remove order location
//     removeLocation: {
//       method: "DELETE",
//       url: `${API_URL}/admin/orders/:orderId/location`,
//     },

//     // Edit customer phone number
//     editPhone: {
//       method: "PUT",
//       url: `${API_URL}/admin/orders/:orderId/phone-number`,
//     },

//     // Return order to shipper
//     returnToShipper: {
//       method: "PUT",
//       url: `${API_URL}/admin/orders/:orderId/return-to-shipper`,
//     },

//     // Get job reports for order
//     getReports: {
//       method: "GET",
//       url: `${API_URL}/admin/orders/:id/job-reports`,
//     },

//     // ============================================================================
//     // NEW FIELD: Update Admin Note
//     // ============================================================================
//     // API: PATCH /admin/orders/:orderId/admin-note
//     // Body: { adminNote: string }
//     // Purpose: Add/edit internal admin notes on orders (visible to admins only)
//     // ============================================================================
//     updateAdminNote: {
//       method: "PUT",
//       url: `${API_URL}/admin/orders/:orderId/admin-note`,
      
//       // Custom reducer to update Redux state immediately after save
//       reduce: (state, action) => {
//         if (action.status === "resolved") {
//           return {
//             ...state,
//             items: state.items.map((stateItem) => {
//               return {
//                 ...stateItem,
//                 items: stateItem.items.map((order) => {
//                   if (order.id === action.context.orderId) {
//                     // Update the adminNote field in the order
//                     return {
//                       ...order,
//                       adminNote: action.body.adminNote,
//                     };
//                   }
//                   return order;
//                 }),
//               };
//             }),
//           };
//         }
//         return { ...state };
//       },
//     },

//     // Edit initial address
//     editInitialAddress: {
//       method: "PUT",
//       url: `${API_URL}/admin/orders/:orderId/initial-address`,
//     },

//     // Fetch order status history
//     fetchOrderHistory: {
//       method: "GET",
//       url: `${API_URL}/admin/orders/:orderId/status-history`,
//     },

//     // Fetch return shipper form
//     fetchReturnShipperForm: {
//       method: "GET",
//       url: `${API_URL}/admin/orders/:orderId/return-form`,
//     },

//     // Add orders to job
//     addToJob: {
//       method: "PUT",
//       url: `${API_URL}/admin/jobs/:jobId/orders/add`,
//     },

//     // Remove orders from job
//     removeFromJob: {
//       method: "DELETE",
//       url: `${API_URL}/admin/jobs/:jobId/orders`,
//     },

//     // Get jobs by status
//     getJobsByStatus: {
//       method: "GET",
//       url: `${API_URL}/admin/jobs/status`,
//       reduce: (state, action) => {
//         return {
//           ...state,
//           jobIds: action.body,
//         };
//       },
//     },

//     // Pure Actions (Redux-only, no API)
//     updateNewTicketState: {
//       isPure: true,
//       reduce: updateOrderNewTicketStateReduce,
//     },
//     updateTicketState: {
//       isPure: true,
//       reduce: updateOrderNewTicketStateReduce,
//     },
//   },
// });

// // Set authorization header
// setAuthHeader(getAuthData().accessToken);